import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { pageService } from '../../services/pageService';
import { supabase } from '../../lib/supabase';

const CompanyInformation = () => {
  const [pageData, setPageData] = useState(null);
  const [leadership, setLeadership] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dynamic Real-Time Analytics States
  const [activeUsers, setActiveUsers] = useState('500K+'); // Leave active users as-is
  const [countriesCount, setCountriesCount] = useState('1');
  const [partsCount, setPartsCount] = useState('0');
  const [usersCount, setUsersCount] = useState('0');

  useEffect(() => {
    const loadPageData = async () => {
      try {
        setLoading(true);
        const data = await pageService.getPageSettings('company-information');
        if (data) {
          setPageData(data);
          // Extract active leadership from sections JSON
          setLeadership(data.sections?.leadership?.filter(m => m.isActive !== false) || []);
        }

        // Fetch real analytics numbers from database
        const [countriesRes, productsDataRes, usersRes] = await Promise.all([
          supabase.rpc('get_unique_countries_count'),
          supabase.from('products').select('component_count'),
          supabase.from('users').select('*', { count: 'exact', head: true })
        ]);

        if (countriesRes.data !== undefined && countriesRes.data !== null) {
          setCountriesCount(countriesRes.data.toString());
        }

        // Match the frontend parts aggregation logic exactly
        const totalParts = productsDataRes.data?.reduce((sum, item) => sum + (item.component_count || 0), 0) || 0;
        setPartsCount(totalParts > 0 ? `${totalParts.toLocaleString()}+` : '15,000+');

        if (usersRes.count !== undefined && usersRes.count !== null) {
          setUsersCount(usersRes.count.toLocaleString());
        }
      } catch (err) {
        console.error('Error loading company info and analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPageData();
  }, []);

  const milestones = [
  { year: '2006', event: 'Idea Was Hatched', description: 'Started with a frustration locating simple repair parts and the founders passion for building things' },
  { year: '2007', event: 'Designed Our First Logo', description: 'Award Winning Designer Damon Daood set the tone with the gear design' },
  { year: '2010', event: 'Filed First Patent', description: 'Over 1000 Man Hours for the design and 5000 hours research and model development' },
  { year: '2011', event: 'First Customers', description: 'Soft Launch with Aerospace Company Startup' },
  { year: '2013', event: 'First Customers', description: 'First Tradeshow' },
  { year: '2014', event: 'Stealth Operations', description: 'Refocus On 3D Technologies' },
  { year: '2025', event: 'Revamped Data Services', description: 'Built New Completely Streamlined Platform' },
  { year: '2026', event: 'New Platform Revealed', description: 'Invitational Relase of New 2026 Beta Platform' }];


  const values = [
  {
    title: 'Innovation First',
    description: 'We push boundaries in 3D technology to create tomorrow\'s visualization tools today',
    icon: 'Lightbulb'
  },
  {
    title: 'User-Centric',
    description: 'Every decision we make starts with understanding and serving our users\' needs',
    icon: 'Users'
  },
  {
    title: 'Quality Obsessed',
    description: 'We maintain the highest standards in code, design, and user experience',
    icon: 'Award'
  },
  {
    title: 'Collaborative Spirit',
    description: 'We believe great products come from diverse perspectives working together',
    icon: 'Heart'
  }];


  const stats = [
    { label: 'Active Users', value: activeUsers, icon: 'Users' },
    { label: 'Countries', value: countriesCount, icon: 'Globe' },
    { label: '3D Models', value: partsCount, icon: 'Box' },
    { label: 'Team Members', value: usersCount, icon: 'Briefcase' }
  ];


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 text-left">
      <Header />
      
      {/* Hero Section */}
      <div className="container mx-auto px-6 pt-24 pb-16 text-center">
        <div className="max-w-4xl mx-auto mb-16">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            {pageData?.title || 'About OpenGadget'}
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            {pageData?.subtitle || "We're on a mission to bring 3D visualizations to everyone, everywhere. Transforming how products are designed, shared, and experienced online, while making it very easy to identify parts replacements."}
          </p>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats?.map((stat, index) =>
          <div
            key={index}
            className="bg-white rounded-xl p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 border border-slate-100">

              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl w-fit mx-auto mb-4">
                <Icon name={stat?.icon} className="text-white" size={24} />
              </div>
              <div className="text-3xl font-bold text-slate-800 mb-2">{stat?.value}</div>
              <div className="text-slate-600">{stat?.label}</div>
            </div>
          )}
        </div>

        {/* Mission & Vision */}
        <div className="bg-white rounded-2xl p-12 shadow-lg border border-slate-100 mb-16 text-left">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl w-fit mb-6">
                <Icon name="Target" className="text-white" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-slate-800 mb-4">Our Mission</h2>
              <p className="text-slate-600 leading-relaxed">
                To democratize 3D visualization technology, making it accessible and powerful for creators, 
                businesses, and developers worldwide. We believe that everyone should have the tools to bring 
                their ideas to life in stunning 3D.
              </p>
            </div>
            <div>
              <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl w-fit mb-6">
                <Icon name="Eye" className="text-white" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-slate-800 mb-4">Our Vision</h2>
              <p className="text-slate-600 leading-relaxed">
                To become the global standard for 3D product visualization, enabling a future where every 
                product online can be explored in immersive 3D. We're building the infrastructure that will 
                power the next generation of e-commerce and digital experiences.
              </p>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">Our Core Values</h2>
            <p className="text-xl text-slate-600">The principles that guide everything we do</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {values?.map((value, index) =>
            <div
              key={index}
              className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100">

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex-shrink-0">
                    <Icon name={value?.icon} className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 mb-3">{value?.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{value?.description}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Leadership Team */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">Leadership Team</h2>
            <p className="text-xl text-slate-600">Meet the people driving our vision forward</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {loading ? (
                [1,2,3,4].map(i => <div key={i} className="animate-pulse bg-white rounded-xl h-80 shadow-lg border border-slate-100" />)
            ) : (
                leadership?.map((leader, index) =>
                <div
                    key={leader.id || index}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-slate-100 text-left">

                    <div className="aspect-square overflow-hidden bg-slate-50 flex items-center justify-center">
                        {leader?.image_url ? (
                            <img
                                src={leader?.image_url}
                                alt={`${leader?.name} - ${leader?.title}`}
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" 
                            />
                        ) : (
                            <Icon name="User" size={64} className="text-slate-200" />
                        )}
                    </div>
                    <div className="p-6">
                        <h3 className="text-xl font-bold text-slate-800 mb-2">{leader?.name}</h3>
                        <p className="text-blue-600 font-semibold mb-3">{leader?.title}</p>
                        <p className="text-sm text-slate-600">{leader?.subtext}</p>
                    </div>
                </div>
                )
            )}
          </div>
        </div>

        {/* Company Timeline */}
        <div className="bg-white rounded-2xl p-12 shadow-lg border border-slate-100 mb-16 text-center">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">Our Journey</h2>
            <p className="text-xl text-slate-600">Key milestones in our growth story</p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-blue-500 to-purple-600"></div>
            <div className="space-y-12">
              {milestones?.map((milestone, index) =>
              <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                    <div className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
                      <div className="text-3xl font-bold text-blue-600 mb-2">{milestone?.year}</div>
                      <h4 className="text-xl font-bold text-slate-800 mb-2">{milestone?.event}</h4>
                      <p className="text-slate-600">{milestone?.description}</p>
                    </div>
                  </div>
                  <div className="w-2/12 flex justify-center">
                    <div className="w-4 h-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full border-4 border-white shadow-lg z-10"></div>
                  </div>
                  <div className="w-5/12"></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Careers CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-12 text-center text-white mb-16">
          <Icon name="Briefcase" size={48} className="mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">Join Our Team</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            We're always looking for talented individuals who are passionate about 3D technology and innovation
          </p>
          <div className="flex justify-center space-x-4">
            <Link to="/careers">
              <Button variant="default" className="bg-white text-blue-600 hover:bg-blue-50 border-none">
                View Open Positions
              </Button>
            </Link>
            <Link to="/careers">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                Learn About Our Culture
              </Button>
            </Link>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-2xl p-12 shadow-lg border border-slate-100">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Get In Touch</h2>
            <p className="text-xl text-slate-600">We'd love to hear from you</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl w-fit mx-auto mb-4">
                <Icon name="Mail" className="text-white" size={24} />
              </div>
              <h4 className="font-semibold text-slate-800 mb-2">Email Us</h4>
              <p className="text-slate-600">contact@opengadget.com</p>
            </div>
            <div>
              <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl w-fit mx-auto mb-4">
                <Icon name="MapPin" className="text-white" size={24} />
              </div>
              <h4 className="font-semibold text-slate-800 mb-2">Mail Us</h4>
              <p className="text-slate-600">PO Box 6073</p>
			  <p className="text-slate-600">McKinney, TX 75070</p>
            </div>
            <div>
              <div className="p-3 bg-gradient-to-br from-pink-500 to-red-600 rounded-xl w-fit mx-auto mb-4">
                <Icon name="Phone" className="text-white" size={24} />
              </div>
              <h4 className="font-semibold text-slate-800 mb-2">Call Us</h4>
              <p className="text-slate-600">+1 (673)-642-3438</p>
			  <p className="text-slate-600">+1 OPEN-GADGET</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>);

};

export default CompanyInformation;
